import { useCallback } from "react"
import { BlockerFunction, Location, useBeforeUnload, useBlocker } from "react-router"

type UseConfirmPageLeaveOptions = {
  onPageLeave?: () => unknown
  onBrowserClose?: () => unknown
  shouldBlockNavigation?: (currentLocation: Location, nextLocation: Location) => boolean
}

/**
 *
 * @param isActive  When "true" and user tries to leave the page, close browser or browser tab, then user will be blocked.
 *
 * You can use "onPageLeave" callback to open your custom dialog when user tries to leave the page.
 *
 * Closing browser or browser's tab will always trigger browser's built-in confirmation alert due to browser's security reasons,
 * but you can still run some code right before closing tab or browser by using "onBrowserClose" callback.
 */
export const useConfirmPageLeave = (
  isActive: boolean,
  { onBrowserClose, onPageLeave, shouldBlockNavigation }: UseConfirmPageLeaveOptions = {},
) => {
  const blockNavigationCallback: BlockerFunction = useCallback(
    ({ currentLocation, nextLocation }) => {
      const shouldBlock = shouldBlockNavigation?.(currentLocation, nextLocation) ?? true
      if (!shouldBlock) return false

      onPageLeave?.()
      return true
    },
    [onPageLeave, shouldBlockNavigation],
  )

  const navigationBlocker = useBlocker(blockNavigationCallback)

  // confirm closing browser tab or whole browser with unsaved changes
  useBeforeUnload(
    useCallback(
      (event) => {
        if (isActive) {
          onBrowserClose?.()
          event.preventDefault() // trigger browser built-in confirmation alert
        }
      },
      [isActive, onBrowserClose],
    ),
  )

  return navigationBlocker
}
